import { Callout, Spinner, Tree, type TreeNodeInfo } from "@blueprintjs/core";
import type { Media } from "@package/ui-gql/index-data-access";
import { useCallback, useMemo, useState } from "react";
import { SidebarEntryPath } from "../../../../constants/contants";
import { setItemGridQuery } from "../../../../store/slices/slice-item-grid";
import { setSelectedParentIdHierarchy } from "../../../../store/slices/slice-media";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import {
  MEDIA_FOLDER_LIMIT,
  MEDIA_ROOT_LABEL,
  useMediaFolders,
} from "./useMediaFolders";

type FolderNodeData = {
  path: string[];
};

function buildNode(
  folder: Media,
  parentPath: string[],
  ancestorIds: Set<string>,
  childrenByParent: Map<string, Media[]>,
  expandedIds: Set<string>,
  activeFolderId: string | undefined,
): TreeNodeInfo<FolderNodeData> {
  const id = folder.id ?? "";
  const path = [...parentPath, id];
  const isExpanded = expandedIds.has(id);
  const children = ancestorIds.has(id) ? [] : (childrenByParent.get(id) ?? []);

  return {
    id,
    label: folder.name,
    icon: isExpanded ? "folder-open" : "folder-close",
    isExpanded,
    isSelected: activeFolderId === id,
    hasCaret: children.length > 0,
    nodeData: { path },
    childNodes: children.map((child) =>
      buildNode(
        child,
        path,
        new Set(ancestorIds).add(id),
        childrenByParent,
        expandedIds,
        activeFolderId,
      ),
    ),
  };
}

export function MediaFolderTree() {
  const dispatch = useAppDispatch();
  const selectedParentIdHierarchy = useAppSelector(
    (state) => state.media.selectedParentIdHierarchy,
  );
  const activeFolderId =
    selectedParentIdHierarchy[selectedParentIdHierarchy.length - 1];

  const { rootFolder, childrenByParent, loading, error, truncated } =
    useMediaFolders();

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const effectiveExpandedIds = useMemo(
    () => new Set([...expandedIds, ...selectedParentIdHierarchy]),
    [expandedIds, selectedParentIdHierarchy],
  );

  const contents = useMemo<TreeNodeInfo<FolderNodeData>[]>(() => {
    if (!rootFolder?.id) return [];

    const rootPath = [rootFolder.id];
    const rootAncestors = new Set([rootFolder.id]);
    const rootChildren = childrenByParent.get(rootFolder.id) ?? [];

    return [
      {
        id: rootFolder.id,
        label: MEDIA_ROOT_LABEL,
        icon: "home",
        isExpanded: true,
        hasCaret: false,
        isSelected: activeFolderId === rootFolder.id,
        nodeData: { path: rootPath },
        childNodes: rootChildren.map((child) =>
          buildNode(
            child,
            rootPath,
            rootAncestors,
            childrenByParent,
            effectiveExpandedIds,
            activeFolderId,
          ),
        ),
      },
    ];
  }, [activeFolderId, childrenByParent, effectiveExpandedIds, rootFolder]);

  const handleNodeClick = useCallback(
    (node: TreeNodeInfo<FolderNodeData>) => {
      if (node.nodeData?.path) {
        dispatch(setSelectedParentIdHierarchy(node.nodeData.path));
        dispatch(setItemGridQuery({ path: SidebarEntryPath.media, query: "" }));
      }
    },
    [dispatch],
  );

  const handleNodeExpand = useCallback((node: TreeNodeInfo<FolderNodeData>) => {
    setExpandedIds((prev) => new Set(prev).add(String(node.id)));
  }, []);

  const handleNodeCollapse = useCallback(
    (node: TreeNodeInfo<FolderNodeData>) => {
      setExpandedIds((prev) => {
        const next = new Set(prev);
        next.delete(String(node.id));
        return next;
      });
    },
    [],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size={20} />
      </div>
    );
  }

  if (error) {
    return (
      <Callout intent="danger">Failed loading media folder structure.</Callout>
    );
  }

  return (
    <>
      <Tree
        contents={contents}
        onNodeClick={handleNodeClick}
        onNodeExpand={handleNodeExpand}
        onNodeCollapse={handleNodeCollapse}
      />
      {truncated && (
        <p className="px-2 text-xs text-brand-muted">
          Showing the first {MEDIA_FOLDER_LIMIT.toLocaleString()} folders.
        </p>
      )}
    </>
  );
}
